import React from 'react';
import FieldTextValue from '../shared/FieldTextValue.jsx';

import { Clock, MapPin } from 'lucide-react';
import { isDisplayableRange } from '../utils/helpers.js';
import SpeakerCard from './SpeakerCard.jsx';
import ExpandableBio from '../ui-components/ExpandableBio.jsx';
import AddToCalendar from '../shared/AddToCalendar.jsx';
import SponsoredBy from '../shared/SponsoredBy.jsx';
import { STATIC_VISIBLE_SESSION_FIELDS } from './constants.js';
import { useI18n } from '../i18n/index.jsx';
import { groupSpeakersBySlug, roleGroupHeading } from '../utils/group-speakers-by-role.js';

export default function SessionCard({
  sessionData = null,
  speakers = [],
  onSessionClick,
  onSpeakerClick,
  showAddToCalendar = false,
  showSessionSponsors = false,
  sessionForCalendar = {},
  visibleSessionFields = [],
  visibleSessionFieldsById = {},
  visibleSpeakerFields = [],
  isHighlighted = false,
}) {
  const visibleCustomFields = visibleSessionFields.filter(field => !STATIC_VISIBLE_SESSION_FIELDS.includes(field));
  const { t } = useI18n();
  const getFieldName = fieldId => {
    return (
      visibleSessionFieldsById[fieldId]?.name ||
      fieldId
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    );
  };

  // Get scalar field value from sessionData
  const getFieldValue = fieldName => {
    if (!sessionData) return null;

    const value = sessionData[fieldName];
    if (value === null || value === undefined || value === '' || Array.isArray(value)) return null;

    return value;
  };

  // Get array field value from sessionData (like tags), for dot-separated rendering
  const getFieldListValue = fieldName => {
    if (!sessionData) return null;

    // SB-8942: multi-value custom fields arrive as `<field>List` alongside the
    // comma-joined string, so a value containing a comma stays one item.
    const list = sessionData[`${fieldName}List`];
    if (Array.isArray(list) && list.length > 0) return list;

    const value = sessionData[fieldName];
    if (!Array.isArray(value) || value.length === 0) return null;

    // If it's an array of objects with name property
    if (value[0] && typeof value[0] === 'object' && value[0].name) {
      return value.map(item => item.name);
    }
    return value;
  };
  const handleSessionClick = () => {
    if (onSessionClick && sessionData?.id) {
      onSessionClick(sessionData?.id);
    }
  };

  const handleSpeakerClick = speakerId => {
    if (onSpeakerClick) {
      onSpeakerClick(speakerId);
    }
  };
  // SB-8853: slug-grouped participants with organizer labels.
  const speakerGroups = groupSpeakersBySlug(speakers);

  return (
    <div
      className={`session-card${isHighlighted ? ' session-card--highlighted' : ''}`}
      data-session-id={sessionData?.id || undefined}
    >
      {/* Title and Description Section */}
      <div className="session-card__header">
        <div className="session-card__title-section">
          <h3 className="session-card__title" onClick={handleSessionClick} style={{ cursor: 'pointer' }}>
            {sessionData?.title}
          </h3>

          {sessionData?.description && visibleSessionFields.includes('description') ? (
            <div className="session-card__description-container">
              <ExpandableBio
                htmlContent={sessionData?.description}
                maxLength={300}
                className="session-card__description"
              />
            </div>
          ) : null}
        </div>

        {/* Date and Location */}
        <div className="session-card__date-location-section">
          {isDisplayableRange(sessionData?.range) && (
            <div className="session-card__date-location">
              <Clock className="session-card__icon" />
              <span className="session-card__date-location-text">{sessionData?.range}</span>
            </div>
          )}

          {sessionData?.location && visibleSessionFields.includes('location') ? (
            <div className="session-card__date-location">
              <MapPin className="session-card__icon" />
              <span className="session-card__date-location-text">{sessionData?.location}</span>
            </div>
          ) : null}

          <AddToCalendar sessionData={sessionForCalendar} enabled={showAddToCalendar} />
        </div>
      </div>

      {/* Speakers Section */}

      {speakers && speakers.length && visibleSessionFields.includes('speakers')
        ? speakerGroups.map(group => (
            <div key={group.slug} className="session-card__speakers">
              <h4 className="session-card__speakers-title">{roleGroupHeading(t, group)}</h4>

              <div className="session-card__speakers-grid">
                {group.speakers.map(speaker => (
                  <SpeakerCard
                    key={speaker.id}
                    speakerId={speaker.id}
                    name={speaker.fullName}
                    title={speaker.title}
                    company={speaker.company}
                    image={speaker.photo}
                    salutation={speaker.salutation}
                    honorific={speaker.honorific}
                    visibleSpeakerFields={visibleSpeakerFields}
                    onSpeakerClick={handleSpeakerClick}
                  />
                ))}
              </div>
            </div>
          ))
        : null}

      {showSessionSponsors && <SponsoredBy sponsors={sessionData?.sponsors} />}

      {/* Session Details */}
      <div className="session-card__details">
        {visibleCustomFields.map(fieldId => {
          const fieldValue = getFieldValue(fieldId);
          const fieldListValue = getFieldListValue(fieldId);

          if (!fieldValue && !fieldListValue) return null;

          return (
            <div key={fieldId} className="session-card__detail-item">
              <span className="session-card__detail-label">{getFieldName(fieldId)}:</span>
              {fieldListValue ? (
                <span className="session-card__detail-value sb-dot-list">
                  {fieldListValue.filter(Boolean).map((value, i) => (
                    <span key={i} className="sb-dot-list__item">
                      {value}
                    </span>
                  ))}
                </span>
              ) : (
                <FieldTextValue html={sessionData?.[`${fieldId}Html`]} className="session-card__detail-value">
                  {fieldValue}
                </FieldTextValue>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
